/**
 * Wire contract for the judge NDJSON stream.
 *
 * `events.schema.json` is the source of truth. These TypeScript types mirror it
 * and are the only thing the app imports, so the browser build never needs the
 * JSON Schema at runtime. If you later generate types (json-schema-to-typescript,
 * quicktype), replace the hand-written types below with the generated ones and
 * keep this file's public API shape.
 */

export interface Entity {
  name: string;
  id?: string | null;
  confidence?: number | null;
}

export interface Source {
  title: string;
  url?: string | null;
  ref?: string | null;
}

export interface Usage {
  inputTokens?: number | null;
  outputTokens?: number | null;
  /** Tokens the model spent on hidden reasoning (billed as output). */
  reasoningTokens?: number | null;
  /** Prompt tokens served from the provider's prompt cache. */
  cachedTokens?: number | null;
  /** Provider-reported cost in USD, when available (not exposed by OpenAI). */
  costUsd?: number | null;
  /** Provider that served the request, for cost diagnosis. */
  provider?: string | null;
}

export interface StartEvent {
  type: "start";
  /**
   * Conversation UUID. This is the single server-owned id for the thread; the
   * client stores it and sends it back on follow-ups.
   */
  id: string;
}

export interface MetaEvent {
  type: "meta";
  cards?: Entity[];
  format?: string | null;
  sources?: Source[];
  /**
   * True when the answer was served from the semantic cache. Only standalone
   * questions can be cached, so this never appears on a contextual follow-up.
   */
  cached?: boolean;
  /**
   * The cached question this answer actually addresses. Set on cached answers
   * whose question differs from the one the user typed, so the UI (and later
   * history views) can show which question the answer belongs to.
   */
  matchedQuestion?: string;
}

export interface MatchEvent {
  type: "match";
  /**
   * Cache entry id. The client echoes it back as `cacheId` to view the cached
   * answer or to ask anyway.
   */
  id: string;
  /**
   * Anonymous telemetry event id for this presentation. The client echoes it
   * back (with `cacheDecision`) so the server can record the choice without
   * linking the event to an account.
   */
  eventId: string;
  /** The similar question whose answer is cached. */
  question: string;
}

export interface TokenEvent {
  type: "token";
  text: string;
}

export interface DoneEvent {
  type: "done";
  usage?: Usage;
}

export interface ErrorEvent {
  type: "error";
  message: string;
  retryable?: boolean;
}

export type JudgeEvent =
  | StartEvent
  | MetaEvent
  | MatchEvent
  | TokenEvent
  | DoneEvent
  | ErrorEvent;

/** Request body POSTed to the judge edge function (NDJSON response). */
export interface JudgeRequest {
  /**
   * Conversation to continue, or `null`/absent to start a new one. The server
   * owns the id; the client only echoes back what it received in `start`.
   */
  conversationId: string | null;
  /** Full conversation so far, oldest first. Last item is the new question. */
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  /**
   * Skip the semantic cache for this request (set by Retry, so a regenerated
   * answer is not replaced by the cached one).
   */
  noCache?: boolean;
  /**
   * Serve the cached answer for this specific cache entry — the "view answer"
   * choice after a semantic match was presented. Free, like any cache hit.
   */
  cacheId?: string;
  /**
   * Anonymous telemetry event id from the presented match. The client echoes
   * it back so the server can update that event with the user's decision.
   */
  eventId?: string;
  /**
   * Regenerate the last turn of `conversationId` in place (set by Retry). The
   * server keeps the conversation and the already-stored user message, drops
   * any stored answer for that turn, and runs the model again. `messages` must
   * be the full thread ending in the retried question; the stale answer is
   * omitted so it never enters the prompt.
   */
  retry?: boolean;
  /**
   * Which choice this request represents after a semantic match was presented.
   * Absent for ordinary questions. `"retry"` accompanies a Retry of a
   * cache-served answer, so the server stamps the linked cache event's
   * `retried_at`.
   */
  cacheDecision?: "view" | "ask_anyway" | "retry";
}

/* ------------------------------------------------------------------ */
/* NDJSON helpers                                                      */
/* ------------------------------------------------------------------ */

/** Serialize one event as an NDJSON line (includes trailing newline). */
export function encodeEvent(event: JudgeEvent): string {
  return `${JSON.stringify(event)}\n`;
}

const EVENT_TYPES = new Set([
  "start",
  "meta",
  "match",
  "token",
  "done",
  "error",
]);

function isJudgeEvent(value: unknown): value is JudgeEvent {
  if (typeof value !== "object" || value === null) return false;
  const type = (value as { type?: unknown }).type;
  if (typeof type !== "string" || !EVENT_TYPES.has(type)) return false;
  if (type === "token") return typeof (value as TokenEvent).text === "string";
  if (type === "error") {
    return typeof (value as ErrorEvent).message === "string";
  }
  if (type === "match") {
    const match = value as MatchEvent;
    return typeof match.question === "string" &&
      typeof match.eventId === "string";
  }
  return true;
}

/**
 * Parse a byte/text stream of newline-delimited JSON into JudgeEvents.
 *
 * Robust to events split across chunks and to a trailing partial line. Unknown
 * or malformed lines are skipped rather than killing the stream, so the client
 * keeps rendering tokens even if the server adds new event types.
 */
export async function* parseEventStream(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<JudgeEvent> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newline: number;
      while ((newline = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, newline).trim();
        buffer = buffer.slice(newline + 1);
        if (!line) continue;
        try {
          const parsed: unknown = JSON.parse(line);
          if (isJudgeEvent(parsed)) yield parsed;
        } catch {
          // Ignore malformed line; keep streaming.
        }
      }
    }

    const tail = (buffer + decoder.decode()).trim();
    if (tail) {
      try {
        const parsed: unknown = JSON.parse(tail);
        if (isJudgeEvent(parsed)) yield parsed;
      } catch {
        // Ignore.
      }
    }
  } finally {
    reader.releaseLock();
  }
}
